import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
  FormArray,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { Day, Location, Time } from './data';
import { Country, COUNTRIES } from '../../services/countries';
import { US_STATES } from '../../shared/constants/us-states.constant';
import {
  US_TIMEZONES,
  DEFAULT_LOCATION_TIMEZONE,
} from '../../shared/constants/us-timezones.constant';
import { ApiService } from '../../services/api.service';
import { ProfileService } from '../../services/profile.service';
import { LocationService } from '../../services/location.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { usaPhoneValidator } from '../../shared/validators/phone.validator';
import { usaZipCodeValidator } from '../../shared/validators/zipcode.validator';
import { strictEmailValidator } from '../../shared/validators/email.validator';
import { standardNameValidator } from '../../shared/validators/name.validator';
import { cityStateValidator } from '../../shared/validators/location.validator';
import { sanitizeErrorMessage } from '../../shared/utils/error.util';
import {
  parseOperatingTimeToHHmm,
  snapHHmmToHalfHour,
} from '../../shared/utils/operating-hours-time.util';

/**
 * Modal component for creating and editing location details including address, contact information, and operating hours.
 * @component
 * @selector app-location-details-modal
 * @example
 *   <app-location-details-modal [locations]="locations" (close)="onClose()" (save)="onSave($event)"></app-location-details-modal>
 * @input Location[] locations - Array of existing locations used to prefill or apply operating hours; updates to this input are handled in ngOnChanges.
 * @output EventEmitter<void> close - Emitted when the modal is closed (canceled or after successful save) to notify parent components to hide the modal.
 * @output EventEmitter<any> save - Emitted after a successful save of a location with the API response payload.
 *
 * Purpose:
 *   Provides a multi-tab modal UI to add or edit a location's address, contact details, and operating hours. It builds and validates a reactive form, offers time selection options, allows importing hours from an existing location, and submits the final payload to an API.
 *
 * Key Methods:
 *   - saveLocation(): Validates the full form, builds the payload (address, contact, formatted hours), posts to the API, emits the save event, closes the modal, shows user feedback, and refreshes location-related caches.
 *   - applyHours(): Fetches operating hours for a selected location via ProfileService and applies them to the form using updateFormHours().
 *   - updateFormHours(hours): Synchronizes fetched hours into both the form controls and the local days array, handling closed days and default times.
 *   - createDayGroup(day): Creates a FormGroup for a single day's operating hours with timeRangeValidator applied.
 *   - timeRangeValidator(group): Ensures close time is strictly after open time when a day is open.
 *   - generateTimes(): Produces a 30-minute increment list of time options used by select controls.
 *   - getFilteredCloseTimes(dayIndex), compareTimes(time1, time2), onOpenTimeChange(dayIndex), formatTimeLabel(time): Helpers for filtering, comparing, formatting times and maintaining valid close times relative to open times.
 *   - setTab(tab), isTabValid(tab), nextTab(), backTab(), isNextButtonDisabled(): Manage multi-tab navigation and enforce per-tab validation rules before advancing.
 *   - onToggleDay(day, dayIndex): Toggles a day's open/closed state and updates corresponding form values and validation.
 *   - closeModal(): Emits the close event to dismiss the modal.
 *
 * Lifecycle Hooks:
 *   - ngOnChanges(changes: SimpleChanges): Watches the locations input and updates internal locations when the input changes.
 */
@Component({
  selector: 'app-location-details-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatSelectModule, FormsModule],
  templateUrl: './location-details-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./location-details-modal.component.scss'],
})
export class LocationDetailsModalComponent implements OnChanges {
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();
  @Input() locations: Location[] = [];

  activeTab = 'Address';
  form: FormGroup;
  times: Time[] = [];
  days: Day[] = [];
  countries: Country[] = COUNTRIES;
  selectedLocation: any;
  hoursApplied = false;

  states = US_STATES;
  timezones = US_TIMEZONES;

  /**
   * Initializes component state and builds a reactive form for location details (address, contact details, and operating hours).
   * @example
   * onstructor(fb, apiService, profileService, locationService, messageService)
   * undefined
   * @param {{FormBuilder}} {{fb}} - Angular FormBuilder used to construct the reactive form and form controls.
   * @param {{ApiService}} {{apiService}} - Service used to perform API calls related to locations and remote data.
   * @param {{ProfileService}} {{profileService}} - Service providing access to user profile and related data.
   * @param {{LocationService}} {{locationService}} - Service for managing location-specific operations and logic.
   * @param {{NotificationService}} {{messageService}} - Service used to display messages, notifications, or alerts to the user.
   * @returns {{void}} No return value; the constructor initializes internal state and the reactive form.
   **/
  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private profileService: ProfileService,
    private locationService: LocationService,
    private messageService: NotificationService
  ) {
    this.times = this.generateTimes();
    this.initializeDays();

    this.form = this.fb.group({
      address: this.fb.group({
        branch: ['', [Validators.required, Validators.maxLength(100), standardNameValidator()]],
        street_address: ['', [Validators.required, Validators.maxLength(200)]],
        city: ['', [Validators.required, Validators.maxLength(100), cityStateValidator()]],
        state: ['', [Validators.required, Validators.maxLength(50), cityStateValidator()]],
        country: ['United States', Validators.required],
        zip_code: ['', [Validators.required, Validators.maxLength(10), usaZipCodeValidator()]],
        timezone: [DEFAULT_LOCATION_TIMEZONE, Validators.required],
      }),
      contactDetails: this.fb.group({
        email: ['', [Validators.required, Validators.maxLength(254), strictEmailValidator()]],
        contact: ['', [Validators.required, Validators.maxLength(15), usaPhoneValidator()]],
      }),
      operatingHours: this.fb.array(this.days.map(day => this.createDayGroup(day))),
    });
  }

  get addressForm(): FormGroup {
    return this.form.get('address') as FormGroup;
  }

  get contactDetailsForm(): FormGroup {
    return this.form.get('contactDetails') as FormGroup;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locations'] && changes['locations'].currentValue) {
      this.locations = changes['locations'].currentValue;
    }
  }

  initializeDays() {
    const daysOrder = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    this.days = daysOrder.map(dayName => ({
      name: dayName,
      isOpen: true,
      openTime: '08:00',
      closeTime: '18:00',
    }));
  }

  createDayGroup(day: Day): FormGroup {
    return this.fb.group(
      {
        name: [day.name],
        isOpen: [day.isOpen],
        openTime: [day.openTime],
        closeTime: [day.closeTime],
      },
      { validators: this.timeRangeValidator }
    );
  }

  /**
   * Validates that a FormGroup's openTime is strictly before closeTime when the day is marked as open.
   * @example
   * imeRangeValidator(formGroupWithTimes)
   * { timeRangeInvalid: true }
   * @param {{FormGroup}} {{group}} - FormGroup containing 'openTime' and 'closeTime' as "HH:mm" strings and 'isOpen' as a boolean control.
   * @returns {{ValidationErrors|null}} Returns { timeRangeInvalid: true } if close time is not after open time, otherwise null.
   **/
  timeRangeValidator(group: FormGroup) {
    const openTime = group.get('openTime')?.value;
    const closeTime = group.get('closeTime')?.value;
    const isOpen = group.get('isOpen')?.value;

    if (!isOpen || !openTime || !closeTime) {
      return null; // No validation needed if day is closed or times are not set
    }

    // Compare times
    const [openHour, openMinute] = openTime.split(':').map(Number);
    const [closeHour, closeMinute] = closeTime.split(':').map(Number);

    const openTotalMinutes = openHour * 60 + openMinute;
    const closeTotalMinutes = closeHour * 60 + closeMinute;

    if (closeTotalMinutes <= openTotalMinutes) {
      return { timeRangeInvalid: true };
    }

    return null;
  }

  /**
   * Generate an array of Time objects for every 30-minute interval across a 24-hour day.
   * @example
   * generateTimes()
   * [{ label: '12:00 AM', value: '00:00' }, { label: '12:30 AM', value: '00:30' }, { label: '01:00 AM', value: '01:00' }, ...]
   * @returns {Time[]} Array of Time objects containing label and value for each 30-minute interval.
   **/
  generateTimes(): Time[] {
    const times: Time[] = [];
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 60; j += 30) {
        const hour = i.toString().padStart(2, '0');
        const minute = j.toString().padStart(2, '0');
        const timeValue = `${hour}:${minute}`;
        times.push({ label: this.formatTimeLabel(timeValue), value: timeValue });
      }
    }
    return times;
  }

  /**
   * Returns an array of closing times filtered to only those strictly after the opening time for the specified day.
   * @example
   * etFilteredCloseTimes(1)
   * [{ value: '10:00' }, { value: '10:30' }]
   * @param {{number}} {{dayIndex}} - Index of the day in the operatingHours FormArray to use for open time lookup.
   * @returns {{Time[]}} Array of Time entries that are after the day's openTime.
   **/
  getFilteredCloseTimes(dayIndex: number): Time[] {
    const operatingHours = this.form.get('operatingHours') as FormArray;
    const dayControl = operatingHours.at(dayIndex) as FormGroup;
    const openTime = dayControl.get('openTime')?.value;

    if (!openTime) {
      return this.times;
    }

    // Filter out times that are before or equal to the open time
    return this.times.filter(time => {
      return this.compareTimes(time.value, openTime) > 0;
    });
  }

  compareTimes(time1: string, time2: string): number {
    const [hour1, minute1] = time1.split(':').map(Number);
    const [hour2, minute2] = time2.split(':').map(Number);

    const totalMinutes1 = hour1 * 60 + minute1;
    const totalMinutes2 = hour2 * 60 + minute2;

    return totalMinutes1 - totalMinutes2;
  }

  /**
   * Update logic when the open time for a specific day changes: clears the close time if it is not after the new open time and triggers validation for that day's FormGroup.
   * @example
   * nOpenTimeChange(1)
   * undefined
   * @param {{number}} {{dayIndex}} - Index of the day in the operatingHours FormArray to update.
   * @returns {{void}} No return value.
   **/
  onOpenTimeChange(dayIndex: number) {
    const operatingHours = this.form.get('operatingHours') as FormArray;
    const dayControl = operatingHours.at(dayIndex) as FormGroup;
    const openTime = dayControl.get('openTime')?.value;
    const closeTime = dayControl.get('closeTime')?.value;

    // If close time is before or equal to open time, clear it
    if (closeTime && openTime && this.compareTimes(closeTime, openTime) <= 0) {
      dayControl.patchValue({ closeTime: null });
    }

    // Trigger validation after changes
    dayControl.updateValueAndValidity();
  }

  formatTimeLabel(time: string): string {
    const [hour, minute] = time.split(':');
    const h = parseInt(hour, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const formattedHour = h % 12 || 12;
    return `${formattedHour}:${minute} ${ampm}`;
  }

  /**
   * Switches the active tab in the location details modal following navigation rules.
   * @example
   * etTab('Contact Details')
   * undefined
   * @param {string} tab - Target tab to activate: 'Address', 'Contact Details', or 'Operating Hours'.
   * @returns {void} Updates this.activeTab when navigating backward always or forward only if the current tab is valid.
   **/
  setTab(tab: string) {
    // Allow navigation to previous tabs always, or to next tab only if current tab is valid
    const tabOrder = ['Address', 'Contact Details', 'Operating Hours'];
    const currentIndex = tabOrder.indexOf(this.activeTab);
    const targetIndex = tabOrder.indexOf(tab);

    // Allow backward navigation (going to previous tabs) always
    // Allow forward navigation only if current tab is valid
    if (targetIndex < currentIndex || this.isTabValid(this.activeTab)) {
      this.activeTab = tab;
    }
  }

  isTabValid(tab: string): boolean {
    switch (tab) {
      case 'Address':
        return this.form.get('address')?.valid ?? false;
      case 'Contact Details':
        return this.form.get('contactDetails')?.valid ?? false;
      default:
        return true;
    }
  }

  /**
   * Toggle the open/closed state for a given operating day and synchronize the corresponding form controls and validations.
   * @example
   * nToggleDay({ isOpen: true, openTime: '09:00', closeTime: '17:00' }, 2)
   * undefined
   * @param {Day} day - The Day object representing the operating day to toggle (contains isOpen, openTime, closeTime).
   * @param {number} dayIndex - The index of the day's FormGroup inside the operatingHours FormArray.
   * @returns {void} Updates the day object and form controls in-place and triggers validation; does not return a value.
   **/
  onToggleDay(day: Day, dayIndex: number) {
    day.isOpen = !day.isOpen;
    const operatingHours = this.form.get('operatingHours') as FormArray;
    const dayControl = operatingHours.at(dayIndex) as FormGroup;

    if (!day.isOpen) {
      day.openTime = null;
      day.closeTime = null;
      // Clear the form control values when day is closed
      dayControl.patchValue({
        isOpen: false,
        openTime: null,
        closeTime: null,
      });
    } else {
      day.openTime = '08:00';
      day.closeTime = '18:00';
      // Set default values when day is opened
      dayControl.patchValue({
        isOpen: true,
        openTime: '08:00',
        closeTime: '18:00',
      });
    }
    // Trigger validation after updating values
    dayControl.updateValueAndValidity();
    this.updateOperatingHoursValidation();
  }

  updateOperatingHoursValidation() {
    // This can be expanded if specific cross-field validation is needed for operating hours
  }

  onLocationChange(event: any) {
    this.selectedLocation = event.value;
    this.hoursApplied = false; // Reset applied state when location changes
  }

  /**
   * Applies stored hours from the currently selected location to the component form by fetching the location details and updating the form and applied state.
   * @example
   * pplyHours()
   * undefined
   * @returns {void} Performs the side-effect of updating the form and setting the hoursApplied flag; does not return a value.
   **/
  applyHours() {
    if (this.selectedLocation && this.selectedLocation.id) {
      this.profileService.getLocationDetails(this.selectedLocation.id).subscribe({
        next: response => {
          const hours = response.data.hours;
          if (hours) {
            this.updateFormHours(hours);
            this.hoursApplied = true; // Set applied state to true after successful application
          }
        },
        error: err => {
          console.error('Failed to get location details', err);
        },
      });
    }
  }

  /**
   * Update the component form controls and local days array with the provided operating hours.
   * @example
   * pdateFormHours([{ day: 'Monday', is_closed: false, open_time: '09:00', close_time: '17:00' }])
   * undefined
   * @param {any[]} hours - Array of operating hour objects to apply to the form and local days.
   * @returns {void} Updates the form array and local state in-place; does not return a value.
   **/
  updateFormHours(hours: any[]) {
    const operatingHours = this.form.get('operatingHours') as FormArray;
    hours.forEach(hour => {
      const dayControl = operatingHours.controls.find(
        control => control.get('name')?.value.toLowerCase() === hour.day.toLowerCase()
      );
      if (dayControl) {
        // Update both form control and local day object
        const dayIndex = operatingHours.controls.indexOf(dayControl);
        const day = this.days[dayIndex];

        if (hour.is_closed) {
          day.isOpen = false;
          day.openTime = null;
          day.closeTime = null;
          dayControl.patchValue({
            isOpen: false,
            openTime: null,
            closeTime: null,
          });
        } else {
          const openHhmm = snapHHmmToHalfHour(parseOperatingTimeToHHmm(hour.open_time));
          const closeHhmm = snapHHmmToHalfHour(parseOperatingTimeToHHmm(hour.close_time));
          day.isOpen = true;
          day.openTime = openHhmm;
          day.closeTime = closeHhmm;
          dayControl.patchValue({
            isOpen: true,
            openTime: openHhmm,
            closeTime: closeHhmm,
          });
        }
      }
    });
    operatingHours.controls.forEach((ctrl, idx) => {
      const g = ctrl as FormGroup;
      if (g.get('isOpen')?.value) {
        this.onOpenTimeChange(idx);
        g.updateValueAndValidity();
      }
    });
  }

  closeModal() {
    this.close.emit();
  }

  isNextButtonDisabled(): boolean {
    switch (this.activeTab) {
      case 'Address':
        return !this.form.get('address')?.valid;
      case 'Contact Details':
        return !this.form.get('contactDetails')?.valid;
      default:
        return false;
    }
  }

  nextTab() {
    if (this.activeTab === 'Address' && this.form.get('address')?.valid) {
      this.activeTab = 'Contact Details';
    } else if (this.activeTab === 'Contact Details' && this.form.get('contactDetails')?.valid) {
      this.activeTab = 'Operating Hours';
    }
  }

  /**
   * Submit a new location if the component form is valid by building a payload from form values, posting it to the API, and handling success and error side effects (emit save, close modal, show messages, clear cache, reload locations).
   * @example
   * aveLocation()
   * undefined
   * @returns {void} Performs side effects and does not return a value.
   */
  saveLocation() {
    if (this.form.valid) {
      const address = this.form.get('address')?.value;
      const contact = this.form.get('contactDetails')?.value;
      const hoursRaw = this.form.get('operatingHours')?.value;

      const payload = {
        branch: address.branch,
        street_address: address.street_address,
        city: address.city,
        state: address.state,
        country: address.country,
        zip_code: address.zip_code,
        timezone: address.timezone,
        email: contact.email,
        contact: contact.contact,
        hours: hoursRaw.map((day: any) => ({
          day: day.name,
          open_time: day.isOpen ? this.formatTimeLabel(day.openTime) : undefined,
          close_time: day.isOpen ? this.formatTimeLabel(day.closeTime) : undefined,
          is_closed: !day.isOpen,
        })),
      };

      this.apiService.post('location/add-location', payload).subscribe({
        next: response => {
          this.save.emit(response);
          this.closeModal();
          this.messageService.add({
            severity: 'success',
            summary: 'Location added!',
            detail: "New location added- '" + address.branch + "'",
          });
          this.locationService.invalidateLocationsFetchCache();
          this.locationService.refreshLocations();
        },
        error: err => {
          console.error('Failed to add location', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: sanitizeErrorMessage(
              err.error?.message || 'Failed to add location. Please try again.'
            ),
          });
          // Handle error appropriately
        },
      });
    }
  }
  backTab() {
    if (this.activeTab === 'Operating Hours') {
      this.activeTab = 'Contact Details';
    } else if (this.activeTab === 'Contact Details') {
      this.activeTab = 'Address';
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
